import { handleGTATracking } from "./gta";
import { handleFacebookPixelTracking } from "./facebook";
import { handleGTMTracking } from "./gtm";
import { handleTikTokPixelTracking } from "./tiktok";
import { handleGoogleAdsTracking } from "./google-ads";
import { handleSnackPixelTracking } from "./snack";
import { useFacebookPixel } from "./helpers/useFacebookPixel";
import { useTiktokPixel } from "./helpers/useTiktokPixel";

export async function createTrackingScripts(tracking, data, existingScripts, options = {}) {
  try {
    if (options.suppressTracking) {
      return;
    }

    const excludedEventNames = options.excludedEventNames || [];
    const trackingConfig = excludedEventNames.length
      ? Object.keys(tracking).reduce((result, key) => {
        const value = tracking[key];
        result[key] = Array.isArray(value)
          ? value.map((pixel) => pixel && Array.isArray(pixel.pixelEvents)
            ? { ...pixel, pixelEvents: pixel.pixelEvents.filter((event) => !excludedEventNames.includes(event.name)) }
            : pixel)
          : value;
        return result;
      }, {})
      : tracking;

    data = normalizeData(data)
    // [FW-4033] Pemanggil yang menunda injeksi pixel sudah memegang event key-nya sejak awal,
    // supaya key itu tetap ikut di payload order walau script-nya baru dimuat saat idle.
    const event_key = options.eventKey || generateTrackingUniqueKey();

    // GTA Tracking
    if (trackingConfig.GTA && trackingConfig.GTALabel) {
      await handleGTATracking(trackingConfig.GTA, trackingConfig.GTALabel, existingScripts);
    }

    // Facebook Pixel Tracking
    if (trackingConfig.FB && trackingConfig.FB.length) {
      useFacebookPixel();
      await handleFacebookPixelTracking(trackingConfig.FB, data, event_key, trackingConfig.globalConversionSetting);
    }

    // Google Tag Manager Tracking
    if (trackingConfig.GTM && trackingConfig.GTM.length) {
      await handleGTMTracking(trackingConfig.GTM, trackingConfig.globalConversionSetting, data.event_key);
    }

    // TikTok Pixel Tracking
    if (trackingConfig.tiktok && trackingConfig.tiktok.length) {
      useTiktokPixel();
      await handleTikTokPixelTracking(trackingConfig.tiktok, data, trackingConfig.globalConversionSetting);
    }

    // Google Ads Tracking
    if (trackingConfig.GA && trackingConfig.GA.length) {
      const conversion = data.transaction_id
        ? { transaction_id: data.transaction_id, value: data.value, currency: data.currency }
        : null;
      await handleGoogleAdsTracking(trackingConfig.GA, existingScripts, trackingConfig.globalConversionSetting, data.event_key, conversion);
    }

    // Snack Pixel Tracking
    if (trackingConfig.snack && trackingConfig.snack.length) {
      await handleSnackPixelTracking(trackingConfig.snack, data, trackingConfig.globalConversionSetting);
    }

    return {
      event_key
    };
  } catch (error) {
    console.error("Error in createTrackingScripts:", error);
  }
}

export function isArchivedBannedDeviceOrder(order = {}) {
  return order.isArchived === true && order.reasonArchived === 'bannedDevice';
}

/**
 * An order's identity in browser event IDs and the once-per-order guard: the checkout's tracking key,
 * or the order id for an order created without one. The server builds its event ID the same way
 * (server/helpers/track.js), so Meta can merge the browser and server copies of an order's event.
 */
export function orderTrackingKey(order = {}) {
  return order.trackingEventKey || order._id;
}

const isPurchaseEvent = (event) => event && event.name === 'Purchase';
// TikTok pixels send nothing while enablePixelEvent is off; Meta pixels have no such switch.
const isActive = (pixel) => pixel && pixel.enablePixelEvent !== false;
const sendsPurchase = (pixel) => isActive(pixel) && Array.isArray(pixel.pixelEvents) && pixel.pixelEvents.some(isPurchaseEvent);
const samePixel = (a, b) => (a._id && a._id === b._id) || (a.pixelId && a.pixelId === b.pixelId);

/**
 * The checkout page never sends Purchase (FW-3846: whether the buyer's device is banned is only known
 * once the order exists). A Purchase the seller set on a checkout-page pixel is sent from the success
 * page instead, with the success page's rules: paid e-payment only, nothing for a banned device, one
 * per order. When the same pixel is already on the success page, Purchase joins that entry (so its
 * Once/Every rule, kept per pixel for TikTok, covers both); if it already sends Purchase, nothing is added.
 * A TikTok entry without manual events ("Install Event Manually" off) keeps its automatic page view
 * and sends only this Purchase.
 */
export function withCheckoutPurchase(successTracking, checkoutTracking = {}) {
  const tracking = { ...successTracking };
  Object.keys(checkoutTracking).forEach((key) => {
    (checkoutTracking[key] || []).filter(sendsPurchase).forEach((pixel) => {
      const purchaseEvents = pixel.pixelEvents.filter(isPurchaseEvent);
      const pixels = Array.isArray(tracking[key]) ? tracking[key].slice() : [];
      const index = pixels.findIndex((successPixel) => successPixel && samePixel(successPixel, pixel));
      if (index === -1) {
        pixels.push({ ...pixel, pixelEvents: purchaseEvents });
      } else if (!isActive(pixels[index])) {
        pixels[index] = { ...pixels[index], enablePixelEvent: true, pixelEvents: purchaseEvents };
      } else if (!sendsPurchase(pixels[index])) {
        pixels[index] = { ...pixels[index], pixelEvents: (pixels[index].pixelEvents || []).concat(purchaseEvents) };
      } else {
        return;
      }
      tracking[key] = pixels;
    });
  });
  return tracking;
}

function normalizeData(data) {
  if (data.event_time) {
    const dateObject = new Date(data.event_time);
    const unixTimestampMs = dateObject.getTime();
    const eventTimeSeconds = Math.floor(unixTimestampMs / 1000);

    data.event_time = eventTimeSeconds;
  }

  return data;
}

export function generateTrackingUniqueKey() {
  const unixTimestampMs = Date.now();
  const sixDigitRandomNumber = Math.floor(100000 + Math.random() * 900000);

  return `${unixTimestampMs}_${sixDigitRandomNumber}`;
}
